import React from "react";
import { isFramedLocation, isComponentPreviewPath } from "../lib/embedRoute";
import { postToParent, EMBED_UNAVAILABLE_MESSAGE } from "../lib/embedHeight";

// Con ~37 rutas en React.lazy, cada navegación descarga un chunk cuyo nombre
// lleva el hash del build. Tras un despliegue, una pestaña abierta (o un
// index.html cacheado) pide chunks del build anterior; como el catch-all de
// Firebase responde /index.html a cualquier ruta, el navegador recibe HTML
// donde espera JavaScript y lanza ChunkLoadError. Sin este boundary, eso
// dejaba #root vacío: pantalla en blanco.
//
// Ante un fallo de carga de chunk recargamos una sola vez (el index.html ya va
// con no-cache, así que la recarga trae los hashes nuevos). El guard en
// sessionStorage evita el bucle de recargas si el fallo es de otra causa.
//
// El guard guarda la HORA de la última recarga y no se borra al arrancar: el
// boundary se monta con el fallback de Suspense ANTES de que lleguen los
// chunks lazy, así que borrarlo en componentDidMount dejaba recargar en bucle
// (decenas de veces por segundo) cuando un chunk falla siempre (CDN caída, un
// antivirus que lo bloquea...). Ahora, si ya se recargó hace menos de
// RELOAD_WINDOW_MS, no se vuelve a recargar y se pinta el respaldo.
const RELOAD_FLAG = "invokeapp:chunk-reload";
const RELOAD_WINDOW_MS = 30000;

export function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || "";
  const message = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(message)
  );
}

/**
 * Recarga UNA vez ante un fallo de carga de chunk (build nuevo desplegado).
 * Exportada para que los boundaries locales (p. ej. el de cada escena de la
 * demo) no se coman el error sin recuperar la página.
 *
 * Devuelve true si ha lanzado la recarga: quien llama no debe dar el contenido
 * por «no disponible» (ni avisar al padre del iframe), porque la página va a
 * volver a cargarse con el build nuevo.
 */
export function reloadOnceForChunkError(error) {
  if (!isChunkLoadError(error)) return false;

  let alreadyReloaded = false;
  try {
    // Valor = Date.now() de la última recarga (un "1" de builds anteriores
    // cuenta como recarga antigua: se puede volver a recargar).
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG)) || 0;
    const now = Date.now();
    alreadyReloaded = last > 0 && now - last >= 0 && now - last < RELOAD_WINDOW_MS;
    if (!alreadyReloaded) window.sessionStorage.setItem(RELOAD_FLAG, String(now));
  } catch (e) {
    // sessionStorage puede estar bloqueado (modo incógnito, cookies de
    // terceros): sin guard preferimos no recargar a arriesgar un bucle.
    alreadyReloaded = true;
  }

  if (alreadyReloaded) return false;
  window.location.reload();
  return true;
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    const reloading = reloadOnceForChunkError(error);
    // Dentro del iframe de CORE-Front se le avisa de que no hay componente
    // para que oculte el bloque. Aquí y no en render() (render puede repetirse
    // y debe ser puro), y nunca si la página ya se está recargando: tras la
    // recarga el componente puede pintarse bien.
    if (!reloading && isFramedLocation() && isComponentPreviewPath(window.location.pathname)) {
      postToParent({ type: EMBED_UNAVAILABLE_MESSAGE });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;

    // Dentro de un iframe (el de una agencia en /embed, o CORE-Front en
    // /playbook-demo/component): el mismo aviso neutro en inglés, dentro de su
    // raíz (si no, la regla de public/index.html lo oculta en /embed). El aviso
    // a CORE-Front de que no hay componente sale de componentDidCatch.
    if (isFramedLocation()) {
      return (
        <div data-invokeapp-embed-root="" lang="en" style={{ padding: "4px 8px", fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif" }}>
          <div style={{ border: "1px solid #e5e3f0", borderRadius: 14, background: "#ffffff", padding: "16px 18px", maxWidth: 480, margin: "0 auto" }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: "#15151f", fontFamily: "inherit" }}>Content not available</p>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b6a80", fontFamily: "inherit" }}>This content is not available at the moment.</p>
          </div>
        </div>
      );
    }

    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 20px",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: 28, marginBottom: 12 }}>Algo ha fallado al cargar la página</h1>
        <p style={{ marginBottom: 24, maxWidth: 520 }}>
          Puede que haya una versión nueva disponible. Vuelve a cargar la página para continuar.
        </p>
        <button type="button" className="z-btn" onClick={() => window.location.reload()}>
          Recargar
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
